/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { IAdminClozeMetricData } from '@datatype/admin/api-type';
import { useAdminClozeMetricQuery } from '@hook/admin/useApi';
import TextBlock from '@component/common/block/text-block';
import Spinner from '@design-system/spinner';

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  width: 280px;
  height: 100%;
  padding: 16px;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_3};
  border-radius: 8px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const Header = styled.div`
  padding-bottom: 8px;
  border-bottom: 1px solid ${color.GRAY_3};
`;

const LanguageSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
`;

const LanguageHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 6px;
`;

const LanguageBadge = styled.div`
  min-width: 28px;
  padding: 2px 6px;
  background-color: ${color.BROWN_2};
  border-radius: 4px;
  text-align: center;
`;

const Card = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background-color: ${color.GRAY_4};
  border-radius: 6px;
`;

const SubGroup = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 4px;
  padding: 8px 12px;
  background-color: ${color.GRAY_4};
  border-radius: 6px;
`;

const SubGroupTitle = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
`;

const SubGroupBody = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const SubGroupItem = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 2px;
  flex: 1;
`;

const LevelMappingRow = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 6px;
  padding: 10px 12px;
  background-color: ${color.BROWN_4};
  border-radius: 6px;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

interface IRowProps {
  label: string;
  value: number;
}

const Row: React.FC<IRowProps> = ({ label, value }) => (
  <Card>
    <TextBlock text={label} design={{ text: { size: 'small', color: color.BLACK_5 } }} />
    <TextBlock
      text={`${value}`}
      design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
    />
  </Card>
);

interface IDifficultyGroupProps {
  title: string;
  easy: number;
  normal: number;
  hard: number;
}

const DifficultyGroup: React.FC<IDifficultyGroupProps> = ({ title, easy, normal, hard }) => (
  <SubGroup>
    <SubGroupTitle>
      <TextBlock text={title} design={{ text: { size: 'small', color: color.BLACK_5 } }} />
    </SubGroupTitle>
    <SubGroupBody>
      <SubGroupItem>
        <TextBlock text='쉬움' design={{ text: { size: 'tiny', color: color.BLACK_5 } }} />
        <TextBlock
          text={`${easy}`}
          design={{ text: { size: 'small', color: color.BLACK_2, bold: true } }}
        />
      </SubGroupItem>
      <SubGroupItem>
        <TextBlock text='보통' design={{ text: { size: 'tiny', color: color.BLACK_5 } }} />
        <TextBlock
          text={`${normal}`}
          design={{ text: { size: 'small', color: color.BLACK_2, bold: true } }}
        />
      </SubGroupItem>
      <SubGroupItem>
        <TextBlock text='어려움' design={{ text: { size: 'tiny', color: color.BLACK_5 } }} />
        <TextBlock
          text={`${hard}`}
          design={{ text: { size: 'small', color: color.BLACK_2, bold: true } }}
        />
      </SubGroupItem>
    </SubGroupBody>
  </SubGroup>
);

interface ILanguageMetricProps {
  label: string;
  total: number;
  vocaEasy: number;
  vocaNormal: number;
  vocaHard: number;
  grammarEasy: number;
  grammarNormal: number;
  grammarHard: number;
  used: number;
  unused: number;
}

const LanguageMetric: React.FC<ILanguageMetricProps> = ({
  label,
  total,
  vocaEasy,
  vocaNormal,
  vocaHard,
  grammarEasy,
  grammarNormal,
  grammarHard,
  used,
  unused,
}) => (
  <LanguageSection>
    <LanguageHeader>
      <LanguageBadge>
        <TextBlock text={label} design={{ text: { size: 'tiny', color: color.WHITE_1, bold: true } }} />
      </LanguageBadge>
    </LanguageHeader>
    <Row label='총 문제수' value={total} />
    <DifficultyGroup title='어휘' easy={vocaEasy} normal={vocaNormal} hard={vocaHard} />
    <DifficultyGroup title='문법' easy={grammarEasy} normal={grammarNormal} hard={grammarHard} />
    <Row label='사용' value={used} />
    <Row label='미사용' value={unused} />
  </LanguageSection>
);

const ClozeMetricPanel: React.FC = () => {
  const { data, isLoading } = useAdminClozeMetricQuery();
  const metric: IAdminClozeMetricData | null = data?.data ?? null;

  if (isLoading || !metric) {
    return (
      <Container>
        <Loader>
          <Spinner size='24px' color={color.BROWN_1} />
        </Loader>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <TextBlock
          text='빈칸문제 지표'
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
        />
      </Header>
      <LanguageMetric
        label='EN'
        total={metric.englishTotalCount}
        vocaEasy={metric.englishVocabularyEasyCount}
        vocaNormal={metric.englishVocabularyNormalCount}
        vocaHard={metric.englishVocabularyHardCount}
        grammarEasy={metric.englishGrammarEasyCount}
        grammarNormal={metric.englishGrammarNormalCount}
        grammarHard={metric.englishGrammarHardCount}
        used={metric.englishUsedCount}
        unused={metric.englishUnusedCount}
      />
      <LanguageMetric
        label='JA'
        total={metric.japaneseTotalCount}
        vocaEasy={metric.japaneseVocabularyEasyCount}
        vocaNormal={metric.japaneseVocabularyNormalCount}
        vocaHard={metric.japaneseVocabularyHardCount}
        grammarEasy={metric.japaneseGrammarEasyCount}
        grammarNormal={metric.japaneseGrammarNormalCount}
        grammarHard={metric.japaneseGrammarHardCount}
        used={metric.japaneseUsedCount}
        unused={metric.japaneseUnusedCount}
      />
      <LevelMappingRow>
        <TextBlock
          text='난이도 ↔ 레벨'
          design={{ text: { size: 'small', color: color.BROWN_1, bold: true } }}
        />
        <TextBlock
          text='쉬움: LV01~04'
          design={{ text: { size: 'tiny', color: color.BLACK_2 } }}
        />
        <TextBlock
          text='보통: LV04~07'
          design={{ text: { size: 'tiny', color: color.BLACK_2 } }}
        />
        <TextBlock
          text='어려움: LV06~10'
          design={{ text: { size: 'tiny', color: color.BLACK_2 } }}
        />
      </LevelMappingRow>
    </Container>
  );
};

export default ClozeMetricPanel;
